import React from 'react';
import { Box, Chip, Tooltip } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { useTranslation } from 'react-i18next';
import { FLAG_SEVERITY, PAYMENT_STATE_COLORS, STATUS_COLORS } from './purchaseUtils';

const SMALL_CHIP = { height: 20, fontSize: 11, fontWeight: 600 };

export function StatusChip({ status, paymentState, inBatch, size = 'small' }) {
  const { t } = useTranslation();
  // Paid invoices are "Maksettu" whatever their workflow status; open batches show "Maksussa".
  if (status === 'approved' && paymentState === 'paid') {
    return <Chip size={size} color="success" label={t('purchaseLedger.paymentState.paid')} sx={SMALL_CHIP} />;
  }
  if (status === 'approved' && inBatch) {
    return <Chip size={size} color="primary" label={t('purchaseLedger.status.in_batch')} sx={SMALL_CHIP} />;
  }
  if (status === 'approved' && paymentState === 'partial') {
    return (
      <Chip size={size} color={PAYMENT_STATE_COLORS.partial} label={t('purchaseLedger.paymentState.partial')} sx={SMALL_CHIP} />
    );
  }
  return (
    <Chip
      size={size}
      color={STATUS_COLORS[status] || 'default'}
      variant={status === 'cancelled' ? 'outlined' : 'filled'}
      label={t(`purchaseLedger.status.${status}`, { defaultValue: status })}
      sx={SMALL_CHIP}
    />
  );
}

export function KindChip({ kind }) {
  const { t } = useTranslation();
  if (!kind || kind === 'invoice') return null;
  return (
    <Chip
      size="small"
      variant="outlined"
      color={kind === 'credit_note' ? 'warning' : 'default'}
      label={t(`purchaseLedger.kind.${kind}`, { defaultValue: kind })}
      sx={SMALL_CHIP}
    />
  );
}

const ICONS = { error: ErrorOutlineIcon, warning: WarningAmberIcon, info: InfoOutlinedIcon };
const COLORS = { error: 'error.main', warning: 'warning.main', info: 'text.disabled' };

/** Small icons with tooltips, one per flag. */
export function FlagIcons({ flags }) {
  const { t } = useTranslation();
  if (!flags || flags.length === 0) return null;
  return (
    <Box sx={{ display: 'inline-flex', gap: 0.25, alignItems: 'center' }}>
      {flags.map((flag) => {
        const severity = FLAG_SEVERITY[flag] || 'info';
        const Icon = ICONS[severity];
        return (
          <Tooltip key={flag} title={t(`purchaseLedger.flags.${flag}`, { defaultValue: flag })}>
            <Icon sx={{ fontSize: 16, color: COLORS[severity] }} data-testid={`pl-flag-${flag}`} />
          </Tooltip>
        );
      })}
    </Box>
  );
}

/** Kohdistus chip: linked / difference / nothing. */
export function LinkChip({ linkCount, flags }) {
  const { t } = useTranslation();
  if (!linkCount) {
    return (
      <Tooltip title={t('purchaseLedger.links.none')}>
        <LinkOffIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
      </Tooltip>
    );
  }
  const diff = (flags || []).includes('match_diff');
  return (
    <Chip
      size="small"
      icon={<LinkIcon sx={{ fontSize: 14 }} />}
      color={diff ? 'warning' : 'success'}
      variant="outlined"
      label={diff ? t('purchaseLedger.links.difference') : t('purchaseLedger.links.linkedCount', { count: linkCount })}
      sx={SMALL_CHIP}
    />
  );
}
